import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import * as STATIC_TAXONOMY from "@/constants/taxonomy";

/**
 * TaxonomyContext — single source of truth for storefront menu + filters.
 *
 * Loads /api/catalog/taxonomy at app mount, with the static `taxonomy.js`
 * constants as offline/SSR fallback so the UI never blocks on the network
 * round-trip. The admin Taxonomy Manager / Menu Manager mutates the same
 * doc via /api/admin/taxonomy and broadcasts a refresh.
 */
const TaxonomyCtx = createContext(null);

export function TaxonomyProvider({ children }) {
  const [data, setData] = useState({
    demographics: STATIC_TAXONOMY.DEMOGRAPHICS,
    categories: STATIC_TAXONOMY.CATEGORIES,
    sub_categories: STATIC_TAXONOMY.SUB_CATEGORIES,
    menu_items: STATIC_TAXONOMY.MEGA_MENU.map((m, i) => ({ ...m, key: m.label.toLowerCase().replace(/[^a-z0-9]+/g, "-"), sort_order: i + 1, is_visible: true })),
    visual_settings: { max_badges: 3, badge_size: "medium", badge_colors: {} },
    spec_presets: {},
  });
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/catalog/taxonomy");
      if (r.data) setData(r.data);
      setLoaded(true);
    } catch {
      // Fall back to static defaults already in state
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    // Defer to idle so /api/catalog/taxonomy doesn't compete with the LCP image
    // fetch on mobile. Static fallback taxonomy is already populated in state,
    // so the UI works fully even before the network call completes.
    const run = () => { refresh(); };
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const id = window.requestIdleCallback(run, { timeout: 2000 });
      return () => { try { window.cancelIdleCallback(id); } catch { /* noop */ } };
    }
    const t = setTimeout(run, 800);
    return () => clearTimeout(t);
  }, [refresh]);

  return (
    <TaxonomyCtx.Provider value={{ ...data, loaded, refresh }}>
      {children}
    </TaxonomyCtx.Provider>
  );
}

export function useTaxonomy() {
  const ctx = useContext(TaxonomyCtx);
  if (!ctx) {
    // Defensive default when used outside provider (storybook, tests)
    return {
      demographics: STATIC_TAXONOMY.DEMOGRAPHICS,
      categories: STATIC_TAXONOMY.CATEGORIES,
      sub_categories: STATIC_TAXONOMY.SUB_CATEGORIES,
      menu_items: STATIC_TAXONOMY.MEGA_MENU,
      visual_settings: { max_badges: 3, badge_size: "medium", badge_colors: {} },
      spec_presets: {},
      loaded: false,
      refresh: () => {},
    };
  }
  return ctx;
}

// Helper lookups — used by Catalog labels
export function labelForDemographic(items, value) {
  return items.find((d) => d.value === value)?.label || value;
}
export function labelForCategory(items, value) {
  return items.find((c) => c.value === value)?.label || value;
}
export function labelForSubCategory(subs, cat, value) {
  return (subs[cat] || []).find((s) => s.value === value)?.label || value;
}
